import { useCallback, useEffect, useRef, useState } from "react";
import { turnstileSiteKey } from "../config/public";
import { useI18n } from "../i18n";

/**
 * Cloudflare Turnstile (ADR 0020). The script is loaded only when a sign-in form renders the widget
 * (explicit rendering), never on the rest of the console. The token is passed to Supabase Auth as
 * `captchaToken`; Supabase verifies it with the secret key on its side.
 */

export const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type RenderOptions = {
  sitekey: string;
  callback: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
  "timeout-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  language?: string;
  action?: string;
  size?: "normal" | "flexible" | "compact";
};

export type TurnstileApi = {
  render(container: HTMLElement, options: RenderOptions): string | undefined;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** Load the Turnstile script once; resolves immediately when `window.turnstile` already exists. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing")));
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error("turnstile script failed"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export type Captcha = {
  /** False when no site key is configured: forms sign in without a token. */
  enabled: boolean;
  siteKey: string;
  token: string | null;
  /** Submit only when this is true. */
  ready: boolean;
  onToken(token: string | null): void;
  /** Call after every attempt: a Turnstile token is single-use. */
  reset(): void;
  resetKey: number;
};

/** Form state for the CAPTCHA token. `siteKey` is injectable for tests. */
export function useCaptcha(siteKey: string = turnstileSiteKey()): Captcha {
  const [token, setToken] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const enabled = siteKey !== "";
  const reset = useCallback(() => {
    setToken(null);
    setResetKey((k) => k + 1);
  }, []);
  return { enabled, siteKey, token, ready: !enabled || token !== null, onToken: setToken, reset, resetKey };
}

/** Renders the Turnstile widget for `captcha`; nothing when no site key is configured. */
export function TurnstileWidget({ captcha, action }: { captcha: Captcha; action?: string }) {
  const { t, lang } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const { enabled, siteKey, onToken, resetKey } = captcha;
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!enabled || !box.current) return;
    let cancelled = false;
    setFailed(false);
    loadTurnstile()
      .then((api) => {
        if (cancelled || !box.current) return;
        widgetId.current = api.render(box.current, {
          sitekey: siteKey,
          theme: "light",
          language: lang,
          action,
          size: "flexible",
          callback: (tok) => onTokenRef.current(tok),
          "expired-callback": () => onTokenRef.current(null),
          "timeout-callback": () => onTokenRef.current(null),
          "error-callback": () => onTokenRef.current(null),
        });
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      const id = widgetId.current;
      widgetId.current = undefined;
      onTokenRef.current(null);
      if (id !== undefined) window.turnstile?.remove(id);
    };
  }, [enabled, siteKey, lang, action]);

  useEffect(() => {
    if (resetKey > 0 && widgetId.current !== undefined) window.turnstile?.reset(widgetId.current);
  }, [resetKey]);

  if (!enabled) return null;
  return (
    <div className="captcha" data-testid="turnstile">
      <div ref={box} className="captcha-box" />
      {failed && (
        <p className="form-error" role="alert">
          {t("login.captcha.loadFailed")}
        </p>
      )}
    </div>
  );
}
